/**
 * Centralized timezone utilities for AleaEdge.
 *
 * Canonical platform timezone is America/Los_Angeles (PST/PDT, DST-aware).
 * The "betting day" rolls over at 3:00 AM PT so late NBA/NHL games that
 * settle past midnight still belong to their original game day.
 *
 * Concepts:
 *  - DISPLAY_TZ          → user-facing timestamps (PT)
 *  - BETTING_TZ          → canonical betting-day timezone (PT)
 *  - BETTING_DAY_ROLLOVER_HOUR → hour at which a new betting day starts
 *
 * NOTE: Edge scanning, settlement timing, Stripe, and historical settled
 * records are NOT bound to these helpers. They continue to operate on UTC
 * timestamps server-side. These helpers only affect display + the
 * frontend "what counts as today" grouping.
 */

export const DISPLAY_TZ = "America/Los_Angeles";
export const BETTING_TZ = "America/Los_Angeles";
export const BETTING_DAY_ROLLOVER_HOUR = 3; // 3:00 AM PT
export const TZ_LABEL = "PT";

function partsInTz(d: Date, tz: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(d);
  const get = (t: string) => Number(parts.find((p) => p.type === t)!.value);
  return {
    y: get("year"), m: get("month"), d: get("day"),
    H: get("hour"), M: get("minute"), S: get("second"),
  };
}

/** UTC instant for the given Y/M/D H:0:0 in tz. */
function tzWallClockToUtc(y: number, m: number, d: number, h: number, tz: string) {
  const guess = Date.UTC(y, m - 1, d, h, 0, 0);
  const p = partsInTz(new Date(guess), tz);
  const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.H, p.M, p.S);
  const offset = asUtc - guess;
  return new Date(guess - offset);
}

/**
 * Returns ISO start/end timestamps for the betting day that contains `now`,
 * with rollover at BETTING_DAY_ROLLOVER_HOUR PT.
 */
export function bettingDayBoundsPT(now = new Date()): { start: string; end: string } {
  const p = partsInTz(now, BETTING_TZ);
  // If we're before the rollover hour, "today" is still yesterday's betting day.
  let y = p.y, m = p.m, d = p.d;
  if (p.H < BETTING_DAY_ROLLOVER_HOUR) {
    const prev = new Date(Date.UTC(y, m - 1, d) - 24 * 3600 * 1000);
    y = prev.getUTCFullYear(); m = prev.getUTCMonth() + 1; d = prev.getUTCDate();
  }
  const start = tzWallClockToUtc(y, m, d, BETTING_DAY_ROLLOVER_HOUR, BETTING_TZ);
  const end = tzWallClockToUtc(y, m, d + 1, BETTING_DAY_ROLLOVER_HOUR, BETTING_TZ);
  return { start: start.toISOString(), end: end.toISOString() };
}

/** YYYY-MM-DD label for the current betting day (PT, 3am rollover). */
export function todayBettingDatePT(now = new Date()): string {
  const { start } = bettingDayBoundsPT(now);
  const p = partsInTz(new Date(start), BETTING_TZ);
  return `${p.y}-${String(p.m).padStart(2, "0")}-${String(p.d).padStart(2, "0")}`;
}

export function nextBettingDatePT(now = new Date()): string {
  const { end } = bettingDayBoundsPT(now);
  const p = partsInTz(new Date(end), BETTING_TZ);
  return `${p.y}-${String(p.m).padStart(2, "0")}-${String(p.d).padStart(2, "0")}`;
}

/** Format an instant in PT, e.g. "Thu, Nov 14 · 7:30 PM PT". */
export function formatPT(
  value: string | number | Date | null | undefined,
  opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" },
  withSuffix = true,
): string {
  if (value == null || value === "") return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "—";
  const s = new Intl.DateTimeFormat("en-US", { ...opts, timeZone: DISPLAY_TZ }).format(d);
  return withSuffix ? `${s} ${TZ_LABEL}` : s;
}

/** Date-only PT label, e.g. "Thursday, November 14". */
export function formatPTDate(
  value: string | number | Date | null | undefined,
  opts: Intl.DateTimeFormatOptions = { weekday: "long", month: "long", day: "numeric" },
): string {
  if (value == null || value === "") return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", { ...opts, timeZone: DISPLAY_TZ }).format(d);
}
